// ts-rest contract for the COAC (corporate-actions) surface.
//
// `CoacAction` is the single COAC model — the legacy `coac_cases` mailbox
// lifecycle was retired (it was orphaned once #26/ADR-0022 removed its
// mailbox-poll feeder; the upload driver only ever wrote CoacAction rows).
//
// Ingestion lands via `POST /api/coac/uploads` (multipart, handled outside
// ts-rest). These endpoints are the presentation projection + the lifecycle
// mutations every COAC view (the routing list, the Ops inbox, the Desk inbox)
// drives:
//
//   GET  /api/coac/actions                      — list CoacAction rows
//   POST /api/coac/actions/:id/route-to-im       — Ops routes to the IM
//   POST /api/coac/actions/:id/record-decision   — IM records their decision
//   POST /api/coac/actions/:id/approve-election   — finalise the election
//   POST /api/coac/actions/:id/default-election   — IM elects the default
//
// Each mutation maps onto a CoacAction Action (`route-ca-to-im`,
// `record-coac-decision`, `approve-ca-election`, `default-ca-election`); the
// controller dispatches via `registry.invoke` and the Action's persona
// declaration is the real ACL (ADR-0002).

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// CoacAction row — the presentation projection of the `CoacAction` ontology
// Object Type. This is the single wire shape every CoacAction-backed view
// consumes; the controller maps `ctx.read(CoacAction)` onto it. Field names
// mirror the ontology (camelCase); date fields are plain strings because the
// parser emits naive ISO timestamps (no offset), which `z.string().datetime()`
// would reject.
// ---------------------------------------------------------------------------

export const coacElectionStatusSchema = z.enum([
    'Pending',
    'RoutedToIm',
    'ElectionApproved',
    'ElectionDefaulted',
]);
export type CoacElectionStatus = z.infer<typeof coacElectionStatusSchema>;

// Ops-completion lifecycle (WP-E) — DISTINCT from electionStatus. A CA stays
// `AwaitingResponses` even after every IM has responded; it moves to `Completed`
// only when an Ops user presses Complete. Completed CAs are read-only on the Desk.
export const coacOpsStatusSchema = z.enum(['AwaitingResponses', 'Completed']);
export type CoacOpsStatus = z.infer<typeof coacOpsStatusSchema>;

export const coacRoutingOutcomeSchema = z.enum([
    'auto_routed',
    'partial',
    'ambiguous',
    'unrouted',
    'pending',
]);
export type CoacRoutingOutcome = z.infer<typeof coacRoutingOutcomeSchema>;

export const coacUrgencyRatingSchema = z.enum(['critical', 'high', 'medium', 'low']);
export type CoacUrgencyRating = z.infer<typeof coacUrgencyRatingSchema>;

// How the responsible IM set (`assignedImUserIds`) was derived.
export const coacAssignmentSourceSchema = z.enum(['alias', 'holdings', 'manual']);
export type CoacAssignmentSource = z.infer<typeof coacAssignmentSourceSchema>;

// Ops review gate: `proposed` = auto-derived, awaiting confirmation; `confirmed`
// = Ops-verified or a manual route.
export const coacAssignmentStatusSchema = z.enum(['proposed', 'confirmed']);
export type CoacAssignmentStatus = z.infer<typeof coacAssignmentStatusSchema>;

// Which rule gave `CoacActionRow.responseDeadline`. `custodian` = the internal
// deadline is equal to the custodian date. `titan_lead_time` = the internal
// deadline is earlier than the custodian date, because Titan needs the lead
// time. Set `titan_lead_time` only when the two dates are different.
export const coacDeadlineBasisSchema = z.enum(['custodian', 'titan_lead_time']);
export type CoacDeadlineBasis = z.infer<typeof coacDeadlineBasisSchema>;

// THE TWO DEADLINE DATES ARE TWO TYPES ON THE WIRE.
//
// Both dates are an ISO string. A plain `string | null` therefore lets a
// consumer read one where the other belongs, and a consumer that reads the
// custodian date acts two working days late. The two types below stop that at
// compile time. A consumer states which date it wants, and a raw string cannot
// answer either question.
//
// The pair mirrors `vendor/septima-ontology-wealth/packages/ontology-wealth/src/coac/internal-deadline.ts`,
// which is the ONLY mint. The declaration is repeated here instead of imported,
// because a contract holds nothing ontological (ADR-0003) and this package does
// not depend on the ontology. The phantom key is a plain string literal, so the
// two declarations are the SAME type to the compiler. `CoacService.listActions`
// assigns the minted ontology value into `CoacActionRow`, so the server
// typecheck fails the moment the two declarations drift apart.
//
// The brand costs nothing at run time. Each schema below is the SAME
// `z.string()` instance under a narrower static type, so the validation and the
// parsed value are unchanged. `wireDateSchema` is the step that lets the two
// casts stay plain: `ZodType` is invariant in its output, so a cast straight
// from `ZodString` needs `as unknown as`, and the cast budget refuses that.

/** The date that the FIRM must meet. It is derived, and nothing stores it. */
export type InternalResponseDeadline = string & { readonly coacDeadlineKind: 'internal' };

/** The date that the CUSTODIAN printed on the notice. */
export type CustodianResponseDeadline = string & { readonly coacDeadlineKind: 'custodian' };

/** A plain wire date. The two schemas below narrow its INFERRED type. */
const wireDateSchema: z.ZodType<string, z.ZodTypeDef, string> = z.string();

/** A `z.string()` whose INFERRED type is the internal deadline. */
const internalResponseDeadlineSchema = wireDateSchema as z.ZodType<
    InternalResponseDeadline,
    z.ZodTypeDef,
    string
>;

/** A `z.string()` whose INFERRED type is the custodian deadline. */
const custodianResponseDeadlineSchema = wireDateSchema as z.ZodType<
    CustodianResponseDeadline,
    z.ZodTypeDef,
    string
>;

// ---------------------------------------------------------------------------
// The election options that a corporate-action notice prints (SEP-251).
//
// This is a RESTATEMENT of the ontology shape, not an import of it: the
// contract must not depend on the ontology runtime (ADR-0003). The ontology
// holds the rules of the list — no blank code, no blank label, no duplicate
// code, at most one default — in
// `vendor/septima-ontology-wealth/packages/ontology-wealth/src/coac/election-option.ts`, and the server
// applies them at the approve seam. The wire carries the read result.
//
// THREE STATES, AND THEY STAY APART:
//
//   null : nobody read a notice for the row. Every workbook row is here.
//   []   : a reader read the notice, and the notice prints no election option.
//   list : the printed options, in the printed order.
//
// A surface that merges `null` and `[]` states that a workbook row has no
// choices, which nobody knows.
// ---------------------------------------------------------------------------

export const coacElectionOptionSchema = z.object({
    /** The code that the notice prints against the option, for example `001`. */
    code: z.string(),
    /** The text of the option, exactly as the notice states it. */
    label: z.string(),
    /** True only when the notice marks this option as the default. */
    isDefault: z.boolean(),
});
export type CoacElectionOption = z.infer<typeof coacElectionOptionSchema>;

export const coacElectionOptionsSchema = z.array(coacElectionOptionSchema);

/**
 * Why a portfolio owes the custodian no election (SEP-251 request 7).
 *
 * The client does not hold the security any more, so no option of the notice
 * applies. The portfolio is RESOLVED, and it is not a No-Vote.
 */
export const coacExclusionReasonSchema = z.enum(['client_sold', 'client_transferred_out']);
export type CoacExclusionReason = z.infer<typeof coacExclusionReasonSchema>;

/**
 * The supporting link of a corporate action (SEP-360 section 6): a web page
 * with the terms of the event, which Ops adds when the notice gives none.
 */
export const coacSupportingLinkSchema = z.object({
    url: z.string(),
    label: z.string().nullable(),
    updatedAt: z.string().nullable(),
    updatedById: z.string().nullable(),
});
export type CoacSupportingLink = z.infer<typeof coacSupportingLinkSchema>;

export const coacActionRowSchema = z.object({
    id: z.string(),
    uploadBatchId: z.string().nullable(),
    securityName: z.string().nullable(),
    securityId: z.string().nullable(),
    eventType: z.string(),
    importKey: z.string(),
    wealthManager: z.string().nullable(),
    // -----------------------------------------------------------------------
    // DEADLINES — read this before you use `responseDeadline`. SEP-201 changed
    // the meaning of that field. It is no longer the date that the custodian
    // printed. It is now the INTERNAL deadline that Dowgate must meet. For a
    // `ca_pdf` row the internal deadline is two working days before the
    // custodian date, because Titan needs that lead time. For every other
    // source the two dates are equal. The custodian date stays on the wire,
    // verbatim, as `custodianResponseDeadline`. `deadlineBasis` names the rule
    // that gave `responseDeadline`. The field keeps its name on purpose: a
    // consumer that nobody updated shows the earlier date, which fails safe.
    // Both dates are plain strings (naive ISO, no offset), the same as the
    // other date fields in this file, and each one carries its own compile-time
    // type, so a consumer cannot read one for the other.
    // -----------------------------------------------------------------------
    responseDeadline: internalResponseDeadlineSchema.nullable(),
    custodianResponseDeadline: custodianResponseDeadlineSchema.nullable(),
    deadlineBasis: coacDeadlineBasisSchema.nullable(),
    // The instant when the responses close, as a UTC ISO string (SEP-360). It
    // is the cut-off of the firm (4pm London time for Dowgate) on the date of
    // `responseDeadline`. Before it, the Desk can answer. At it, the open
    // portfolios take the default. Null when the deadline is not readable.
    responseClosesAt: z.string().nullable(),
    managerDecision: z.string().nullable(),
    // COAC Book feed (mandatory CA events) — populated only by the "COAC Book"
    // (Book1.xlsx) upload path; null on COAC Log / PDF rows. Dates are plain
    // strings (naive ISO, no offset), matching `responseDeadline` above.
    swiftTag: z.string().nullable(),
    actionType: z.string().nullable(),
    reference: z.string().nullable(),
    sedol: z.string().nullable(),
    responseRequired: z.boolean().nullable(),
    eligibleUnits: z.number().nullable(),
    unrespondedUnits: z.number().nullable(),
    exDate: z.string().nullable(),
    recordDate: z.string().nullable(),
    effectiveDate: z.string().nullable(),
    payDate: z.string().nullable(),
    electionStatus: coacElectionStatusSchema,
    // The options that the notice of THIS corporate action prints. It is the
    // decision list of every affected portfolio. Null means that nobody read a
    // notice, and `[]` means that the notice prints no option. See the three
    // states above.
    electionOptions: coacElectionOptionsSchema.nullable(),
    routingOutcome: coacRoutingOutcomeSchema.nullable(),
    unresolvedManagerTokens: z.array(z.string()).nullable(),
    needsReview: z.boolean().nullable(),
    assigneeId: z.string().nullable(),
    assignedAt: z.string().nullable(),
    assignmentReason: z.string().nullable(),
    // Multi-IM routing (the responsible set + Ops review gate). `assigneeId`
    // above remains the single "lead"; this is the full set every Desk inbox
    // scopes against.
    assignedImUserIds: z.array(z.string()),
    assignmentSource: coacAssignmentSourceSchema.nullable(),
    assignmentStatus: coacAssignmentStatusSchema.nullable(),
    assignmentReviewedAt: z.string().nullable(),
    assignmentReviewedById: z.string().nullable(),
    urgencyRating: coacUrgencyRatingSchema.nullable(),
    sourceLog: z.string().nullable(),
    isDeleted: z.boolean(),
    createdAt: z.string(),
    // Ops-completion lifecycle (WP-E). `opsStatus` null on legacy rows is treated
    // as `AwaitingResponses` by every consumer.
    opsStatus: coacOpsStatusSchema.nullable(),
    opsCompletedAt: z.string().nullable(),
    opsCompletedById: z.string().nullable(),
    // True when the originating CA upload was a PDF whose raw bytes were
    // retained (only `ca_pdf`-sourced actions ever have one). Drives the
    // "View source PDF" affordance on the Desk card.
    hasSourcePdf: z.boolean(),
    // SEP-360 section 6. The supporting link that Ops added, or null. Ops and
    // the Desk see it on each stage of the workflow.
    supportingLink: coacSupportingLinkSchema.nullable(),
});
export type CoacActionRow = z.infer<typeof coacActionRowSchema>;

export const coacActionListResponseSchema = z.object({
    actions: z.array(coacActionRowSchema),
});
export type CoacActionListResponse = z.infer<typeof coacActionListResponseSchema>;

// ---------------------------------------------------------------------------
// Lifecycle mutation request / response schemas — mirror the CoacAction
// Action inputs the controller dispatches.
// ---------------------------------------------------------------------------

export const coacRouteToImRequestSchema = z.object({
    // The IM (users row id) Ops picked to own the action. Optional — omitting it
    // keeps whatever CEDAR auto-matched at upload.
    assigneeId: z.string().uuid().optional(),
    note: z.string().optional(),
});
export type CoacRouteToImRequest = z.infer<typeof coacRouteToImRequestSchema>;

// Bulk multi-IM routing (the manual-routing surface). `routeActionsBulk` routes
// every selected CoacAction to one chosen IM set; `routeActionsToHoldersBulk`
// fans each selected action out to all desk IMs holding its security.
export const coacRouteBulkRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
    imUserIds: z.array(z.string().uuid()).min(1),
});
export type CoacRouteBulkRequest = z.infer<typeof coacRouteBulkRequestSchema>;

export const coacRouteToHoldersBulkRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
export type CoacRouteToHoldersBulkRequest = z.infer<typeof coacRouteToHoldersBulkRequestSchema>;

// Ops review gate: confirm a proposed assignment, optionally correcting the set.
export const coacConfirmAssignmentRequestSchema = z.object({
    imUserIds: z.array(z.string().uuid()).min(1).optional(),
});
export type CoacConfirmAssignmentRequest = z.infer<typeof coacConfirmAssignmentRequestSchema>;

// Recall (SEP-231): Ops takes a routed CA back from some or all of its assigned
// IMs. `imUserIds` names the IMs to REMOVE. Naming every assigned IM is the
// full recall, which returns the CA to the "For Review" bucket; naming a subset
// leaves the CA under "Awaiting IM" for the IMs that remain.
export const coacRecallRequestSchema = z.object({
    imUserIds: z.array(z.string().uuid()).min(1),
    note: z.string().optional(),
});
export type CoacRecallRequest = z.infer<typeof coacRecallRequestSchema>;

export const coacRecallResponseSchema = z.object({
    ok: z.literal(true),
    // The responsible set after the recall.
    remainingImUserIds: z.array(z.string()),
    // True when the recall emptied the set, so the CA went back to For Review.
    returnedToReview: z.boolean(),
});
export type CoacRecallResponse = z.infer<typeof coacRecallResponseSchema>;

// Bulk soft-delete (SEP-231): Ops removes corporate actions that an upload
// should not have produced. Restricted to `Pending` rows — a routed CA leaves
// through a recall, and a completed CA is the audit record.
export const coacDeleteBulkRequestSchema = z.object({
    ids: z.array(z.string().uuid()).min(1),
});
export type CoacDeleteBulkRequest = z.infer<typeof coacDeleteBulkRequestSchema>;

export const coacRecordDecisionRequestSchema = z.object({
    decision: z.string().min(1),
    force: z.boolean().optional(),
});
export type CoacRecordDecisionRequest = z.infer<typeof coacRecordDecisionRequestSchema>;

export const coacApproveElectionRequestSchema = z.object({
    approvalNote: z.string().optional(),
});
export type CoacApproveElectionRequest = z.infer<typeof coacApproveElectionRequestSchema>;

export const coacDefaultElectionRequestSchema = z.object({
    defaultReason: z.string().optional(),
});
export type CoacDefaultElectionRequest = z.infer<typeof coacDefaultElectionRequestSchema>;

const coacActionOkSchema = z.object({ ok: z.literal(true) });
import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Exposure — for a CA security, who holds it and under what service mandate
// (discretionary / advisory / execution-only). IM-scoped server-side: a Desk IM
// sees only their own book; Ops / leadership see firm-wide. Backs the command
// centre's expandable row (the per-action breakdown) and its list-level chips
// + XO sort/filter (the batch summary). Read through Drizzle joins, not the
// ontology, so this is firm-infrastructure aggregation rather than `ctx.read`.
// ---------------------------------------------------------------------------

export const coacServiceTypeSchema = z.enum(['Discretionary', 'Advisory', 'ExecutionOnly']);
export type CoacServiceType = z.infer<typeof coacServiceTypeSchema>;

export const coacExposureBucketSchema = z.object({
    units: z.number(),
    valueGbp: z.number(),
    accounts: z.number(),
});
export type CoacExposureBucket = z.infer<typeof coacExposureBucketSchema>;

export const coacExposureSummarySchema = z.object({
    // SEP-276. The summary is for ONE corporate action, because Operations can
    // add portfolios to, and remove portfolios from, the affected set of each
    // corporate action. Two actions on one security can differ.
    actionId: z.string(),
    securityId: z.string(),
    discretionary: coacExposureBucketSchema,
    advisory: coacExposureBucketSchema,
    executionOnly: coacExposureBucketSchema,
    clientCount: z.number(),
    // Distinct risk bands (Portfolio.riskBand — Growth / Balanced /
    // Adventurous / Cautious / Conservative) present in this security's IM-scoped
    // holdings. Drives the list-level risk filter. Execution-only holdings carry
    // no managed risk band, so they contribute nothing here.
    riskBands: z.array(z.string()),
});
export type CoacExposureSummary = z.infer<typeof coacExposureSummarySchema>;

export const coacExposureSummaryRequestSchema = z.object({
    // SEP-276. The corporate actions to summarise. The server reads the
    // affected set of each one, with the changes that Operations made.
    actionIds: z.array(z.string()).max(500),
});
export type CoacExposureSummaryRequest = z.infer<typeof coacExposureSummaryRequestSchema>;

export const coacExposureLineSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    // The manager of the PORTFOLIO that holds the line (SEP-251). It is the
    // same plane that COAC routing proposes on, so a reader can group the
    // exposure by IM and get the set of managers the routing proposes. Null
    // when the Portfolio names no manager.
    imUserId: z.string().nullable(),
    portfolioRef: z.string(),
    accountType: z.string().nullable(),
    mandateType: z.string().nullable(),
    serviceType: coacServiceTypeSchema,
    // The managed risk band (Portfolio.riskBand) — the second decision axis
    // alongside serviceType. Null on holdings with no banded mandate (most
    // execution-only accounts). Lets the IM elect per (service × risk) group,
    // e.g. one option for Discretionary · Balanced, another for Discretionary · Growth.
    riskBand: z.string().nullable(),
    quantity: z.number(),
    valuationGbp: z.number(),
});
export type CoacExposureLine = z.infer<typeof coacExposureLineSchema>;

export const coacExposureBreakdownSchema = z.object({
    actionId: z.string(),
    securityId: z.string().nullable(),
    securityName: z.string().nullable(),
    lines: z.array(coacExposureLineSchema),
});
export type CoacExposureBreakdown = z.infer<typeof coacExposureBreakdownSchema>;

// ---------------------------------------------------------------------------
// Per-account elections (WP-E) — the per-account decision model. One row per
// (coacActionId, portfolioRef): the decision an IM recorded on a single
// account's holding. `isNoVote` flags a deadline-passed undecided account.
// ---------------------------------------------------------------------------

export const coacAccountElectionRowSchema = z.object({
    id: z.string(),
    coacActionId: z.string(),
    portfolioRef: z.string(),
    clientId: z.string(),
    imUserId: z.string().nullable(),
    mandateType: z.string().nullable(),
    serviceType: coacServiceTypeSchema,
    decision: z.string().nullable(),
    isNoVote: z.boolean(),
    // WHAT KIND OF ROW THIS IS (SEP-251). Read the codes, never the text.
    // A code names one printed option of `CoacAction.electionOptions`.
    // An exclusion says that the client does not hold the security any more.
    // The portfolio is resolved, and it owes the custodian no election.
    //
    // Each one is OPTIONAL on the wire, because the per-IM responses breakdown
    // (`apps/server/src/modules/coac/coac-responses.ts`) still builds this view
    // from a record that carries neither field. An absent value reads the same
    // way as `null`: the row is not a printed election and it is not an
    // exclusion. The Titan slice adds the two fields to that record.
    electionOptionCode: z.string().nullable().optional(),
    exclusionReason: coacExclusionReasonSchema.nullable().optional(),
    // WHO CHOSE THE ROW (SEP-251 request 4). True when the internal deadline
    // applied the option that the notice prints as its default, and no person
    // chose it. It is orthogonal to the two codes above: an automatic default
    // always carries an `electionOptionCode`, because it names that option.
    //
    // There is no matching field on the RECORD request below, and that is
    // deliberate. Only the server-side sweep writes an automatic default. A
    // browser that could declare its own row automatic could put a portfolio
    // out of the reach of its own investment manager.
    //
    // Only Operations replaces an automatic default.
    isAutomaticDefault: z.boolean().optional(),
    // SEP-276. Optional free text that the IM wrote beside the option.
    note: z.string().nullable().optional(),
    // SEP-277. The last override by Operations: why, who and when. Null on a
    // row that no override touched. The Desk reads these to see why an answer
    // changed, and a Desk actor cannot change such a row.
    overrideReason: z.string().nullable().optional(),
    overriddenByUserId: z.string().nullable().optional(),
    overriddenAt: z.string().nullable().optional(),
    decidedByUserId: z.string().nullable(),
    decidedAt: z.string(),
});
export type CoacAccountElectionRow = z.infer<typeof coacAccountElectionRowSchema>;

// Record one account's election. The Desk ticks an account in the holdings
// breakdown and picks its decision; the controller maps this onto the
// RecordCoacAccountElection Action input (denormalising clientId / imUserId /
// mandateType / serviceType from the live exposure line).
export const coacRecordAccountElectionRequestSchema = z.object({
    portfolioRef: z.string().min(1),
    clientId: z.string().min(1),
    imUserId: z.string().uuid().nullable().optional(),
    mandateType: z.string().nullable().optional(),
    serviceType: coacServiceTypeSchema,
    // The readable text of the answer. An exclusion sends the words of the
    // exclusion here too, so a reader that nobody updated shows the row.
    decision: z.string().min(1),
    // The code of the printed option that the IM chose. Leave it out for a
    // free-text answer. The Action refuses a code that the notice does not
    // print.
    electionOptionCode: z.string().min(1).nullable().optional(),
    // Send this instead of an option code when the client does not hold the
    // security any more. The Action refuses both together.
    exclusionReason: coacExclusionReasonSchema.nullable().optional(),
    // SEP-276. Optional free text beside the option. Leave it out to keep the
    // note that the row holds. Send null or blank text to clear it.
    note: z.string().max(2000).nullable().optional(),
    // SEP-277. Why Operations changes an answer that the portfolio already
    // holds. The Action refuses such a change without it, and it refuses a
    // reason from a Desk actor.
    overrideReason: z.string().max(2000).nullable().optional(),
});
export type CoacRecordAccountElectionRequest = z.infer<
    typeof coacRecordAccountElectionRequestSchema
>;

// Per-account election plus the joined client name (for the per-IM breakdown +
// the Responses tab, which render the person, not just the portfolioRef).
export const coacAccountElectionViewSchema = coacAccountElectionRowSchema.extend({
    clientName: z.string(),
});
export type CoacAccountElectionView = z.infer<typeof coacAccountElectionViewSchema>;

// One assigned IM's response state on a CA, for the expandable per-IM breakdown
// and the Routing list's "Responded" / "Voted" columns. `respondedAccounts` /
// `totalAccounts` drive the "8/10" per-IM rollup; `initials` feeds the list-
// level "JS, NM" responded column.
export const coacImResponseSchema = z.object({
    imUserId: z.string(),
    imName: z.string(),
    initials: z.string(),
    respondedAccounts: z.number(),
    totalAccounts: z.number(),
    hasResponded: z.boolean(),
    // SEP-231. False when this IM owns accounts on the CA but is NOT in the
    // responsible set — because Ops recalled them, or narrowed the set at the
    // review gate. The accounts STAY in `totalAccounts`: the firm still owes
    // the custodian an answer for every holding, and a recall only says who
    // answers. The group renders as "Not assigned" and never counts toward
    // `respondedInitials`, so the gap is visible instead of silent. True for
    // the "Unassigned" group (accounts with no owning IM) and true whenever
    // the CA carries no responsible set at all.
    isAssigned: z.boolean(),
    // The account-level elections this IM has recorded (with client names),
    // grouped under the IM in the breakdown.
    elections: z.array(coacAccountElectionViewSchema),
});
export type CoacImResponse = z.infer<typeof coacImResponseSchema>;

// The full per-CA responses breakdown: the voted/total roll-up + the per-IM
// detail. Backs the Ops Routing list's expandable row and feeds the columns.
export const coacResponsesBreakdownSchema = z.object({
    actionId: z.string(),
    votedAccounts: z.number(),
    totalAccounts: z.number(),
    // Distinct IM initials that have fully responded (all their accounts decided).
    respondedInitials: z.array(z.string()),
    ims: z.array(coacImResponseSchema),
});
export type CoacResponsesBreakdown = z.infer<typeof coacResponsesBreakdownSchema>;

// Compact per-CA response summary for the Routing list's columns (voted/total +
// responded IM initials), batched across every CA in one call.
export const coacResponsesSummaryRowSchema = z.object({
    actionId: z.string(),
    votedAccounts: z.number(),
    totalAccounts: z.number(),
    respondedInitials: z.array(z.string()),
});
export type CoacResponsesSummaryRow = z.infer<typeof coacResponsesSummaryRowSchema>;

// ---------------------------------------------------------------------------
// The affected-portfolio tree (SEP-251) — every portfolio a corporate action
// touches, answered or not, grouped IM -> client -> portfolio.
//
// The responses breakdown above answers "who has replied?". This answers "what
// does the firm owe an answer for?", which is a different question and the one
// the Ops COAC Mailbox asks at each of its stages. The set is the live
// exposure of the security, joined to the recorded elections, so a portfolio
// with no election is a row here and nowhere else.
// ---------------------------------------------------------------------------

/**
 * The response state of ONE portfolio on ONE corporate action.
 *
 *  - `awaiting`: no election is recorded. The firm still owes an answer.
 *  - `answered`: an IM (or Ops for them) recorded a decision.
 *  - `no_vote`: the deadline passed with no decision, and the completion sweep
 *    wrote the No-Vote election. It is resolved, and it is not an answer, so it
 *    is its own value and not a flavour of `answered`.
 *
 * AN EXCLUSION IS `answered` (SEP-251 request 7). "Client Sold" and "Client
 * Transferred Out" say that the client does not hold the security any more.
 * The firm still replies to the custodian, and the reply is that the portfolio
 * needs no election, so the portfolio is answered and it leaves the awaiting
 * count. It takes no value of its own here, because this enum answers one
 * question: does the firm still owe an answer? An exclusion answers it. WHY the
 * portfolio holds no election is a different fact, and `exclusionReason` on the
 * election row carries it.
 */
export const coacPortfolioResponseStatusSchema = z.enum(['awaiting', 'answered', 'no_vote']);
export type CoacPortfolioResponseStatus = z.infer<typeof coacPortfolioResponseStatusSchema>;

/** One affected portfolio, with the response it carries (or does not). */
export const coacAffectedPortfolioSchema = z.object({
    portfolioRef: z.string(),
    accountType: z.string().nullable(),
    // Who decides: on a discretionary holding the IM elects, and on an
    // execution-only holding the firm must ask the client (ADR-0022).
    serviceType: coacServiceTypeSchema,
    // The size of the affected position. It is why the portfolio is on the list.
    quantity: z.number(),
    // The custodian that holds the account — the "Location" of the Titan export.
    custodian: z.string().nullable(),
    // The service classification of the product wrapper. Operations sends it
    // back when it overrides an automatic default, so the replacement row keeps
    // the classification that the portfolio carried.
    mandateType: z.string().nullable(),
    decision: z.string().nullable(),
    isNoVote: z.boolean(),
    // The printed option that the row names, and whether the internal deadline
    // chose it (SEP-251 request 4). Operations reads these two to tell an
    // automatic default from an answer that an investment manager gave.
    electionOptionCode: z.string().nullable(),
    isAutomaticDefault: z.boolean(),
    // SEP-276. The note that the IM wrote beside the option, or null.
    note: z.string().nullable(),
    // SEP-277. Why Operations overrode the answer and when. Null when no
    // override touched the row. The reason stays with the response.
    overrideReason: z.string().nullable(),
    overriddenAt: z.string().nullable(),
    decidedAt: z.string().nullable(),
    status: coacPortfolioResponseStatusSchema,
    // SEP-276. `platform` when the holdings put the portfolio in the set, and
    // `added` when Operations added it by hand.
    source: z.enum(['platform', 'added']),
    // SEP-276. The quantity that Operations typed for an added portfolio, or
    // null. `quantity` above already carries it when it is set.
    manualQuantity: z.number().nullable(),
    // SEP-276. True for an added portfolio when the platform found no holding
    // of the security and Operations typed no quantity. `quantity` is then 0.
    quantityMissing: z.boolean(),
});
export type CoacAffectedPortfolio = z.infer<typeof coacAffectedPortfolioSchema>;

/**
 * One portfolio of one IM, as Operations sees it when it adds portfolios to a
 * corporate action (SEP-276).
 */
export const coacImPortfolioSchema = z.object({
    portfolioRef: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    accountType: z.string().nullable(),
    custodian: z.string().nullable(),
    mandateType: z.string().nullable(),
    serviceType: coacServiceTypeSchema,
    // True when the portfolio is in the affected set already, from the
    // holdings or from an earlier add.
    alreadyAffected: z.boolean(),
    // The quantity of the security that the platform found in the portfolio,
    // or null when it found none.
    quantity: z.number().nullable(),
});
export type CoacImPortfolio = z.infer<typeof coacImPortfolioSchema>;

export const coacAddPortfoliosRequestSchema = z.object({
    imUserId: z.string(),
    portfolioRefs: z.array(z.string().min(1)).min(1).max(200),
});
export type CoacAddPortfoliosRequest = z.infer<typeof coacAddPortfoliosRequestSchema>;

export const coacRemovePortfolioRequestSchema = z.object({
    portfolioRef: z.string().min(1),
});
export type CoacRemovePortfolioRequest = z.infer<typeof coacRemovePortfolioRequestSchema>;

export const coacSetPortfolioQuantityRequestSchema = z.object({
    portfolioRef: z.string().min(1),
    // Null clears the quantity. The quantity is optional.
    quantity: z.number().nonnegative().nullable(),
});
export type CoacSetPortfolioQuantityRequest = z.infer<typeof coacSetPortfolioQuantityRequestSchema>;

/** One client of one IM group, with each affected portfolio beneath them. */
export const coacAffectedClientSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    portfolios: z.array(coacAffectedPortfolioSchema),
});
export type CoacAffectedClient = z.infer<typeof coacAffectedClientSchema>;

/**
 * One suggested (or assigned) IM, with the clients they hold on the action.
 *
 * `imUserId` is null for the group of portfolios that name no manager. That
 * group carries the name "Unassigned", which is the word the per-IM responses
 * breakdown already uses for the same case.
 */
export const coacAffectedImGroupSchema = z.object({
    imUserId: z.string().nullable(),
    imName: z.string(),
    clients: z.array(coacAffectedClientSchema),
});
export type CoacAffectedImGroup = z.infer<typeof coacAffectedImGroupSchema>;

export const coacAffectedPortfoliosSchema = z.object({
    actionId: z.string(),
    securityId: z.string().nullable(),
    securityName: z.string().nullable(),
    // The count of portfolios that still owe an answer, across every group.
    awaitingCount: z.number(),
    // The count of portfolios in the tree, across every group.
    portfolioCount: z.number(),
    groups: z.array(coacAffectedImGroupSchema),
});
export type CoacAffectedPortfolios = z.infer<typeof coacAffectedPortfoliosSchema>;

// One flat row for the Ops "Responses" tab: every account election across every
// CA, joined to its CA + client + IM for a filterable table.
export const coacResponsesRowSchema = z.object({
    actionId: z.string(),
    securityName: z.string().nullable(),
    securityId: z.string().nullable(),
    eventType: z.string(),
    portfolioRef: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    imUserId: z.string().nullable(),
    imName: z.string().nullable(),
    serviceType: coacServiceTypeSchema,
    mandateType: z.string().nullable(),
    decision: z.string().nullable(),
    isNoVote: z.boolean(),
    // The kind of the row (SEP-251). See `coacAccountElectionRowSchema`.
    electionOptionCode: z.string().nullable(),
    exclusionReason: coacExclusionReasonSchema.nullable(),
    // True when the internal deadline chose the row (SEP-251 request 4).
    isAutomaticDefault: z.boolean(),
    decidedAt: z.string(),
    opsStatus: coacOpsStatusSchema.nullable(),
});
export type CoacResponsesRow = z.infer<typeof coacResponsesRowSchema>;

// Complete a CA: the controller writes No-Vote elections for any undecided
// account whose deadline has passed, then dispatches CompleteCoacAction.
export const coacCompleteActionRequestSchema = z.object({
    note: z.string().optional(),
});
export type CoacCompleteActionRequest = z.infer<typeof coacCompleteActionRequestSchema>;

// SEP-349. The structured CEDAR explanation of a corporate action: the event,
// and the meaning and the outcome of each election option of the notice. A
// fact that the notice does not state reads "Not stated in the notice".
export const coacBreakdownExplanationSchema = z.object({
    event: z.string(),
    options: z.array(
        z.object({
            code: z.string().nullable(),
            label: z.string(),
            meaning: z.string(),
            outcome: z.string(),
        }),
    ),
});
export type CoacBreakdownExplanation = z.infer<typeof coacBreakdownExplanationSchema>;

// ---------------------------------------------------------------------------
// CoacActionDraft — AgentArtefact + Reviewable CA draft emitted by MASON's
// `coac_pdf_extraction` job. (ADR-0022 #271 #272)
//
// Approve / reject go through the generic `/agent/reviewables/decisions`
// pipeline (kind `coac-action-draft`) — see `apps/web/src/lib/agent-reviewables.ts`
// — not a bespoke route here. `listActionDrafts` below is a read-only listing.
// ---------------------------------------------------------------------------

// One date on a draft, as the CEDAR PDF extractor stored it. The extractor
// stores the text of the notice verbatim. The draft holds no parsed twin,
// because the reading is a pure function of the verbatim text, and a stored
// twin goes stale. The server does the reading at the seam and projects it
// here.
export const noticeDateSchema = z.object({
    // The date text that the notice printed, verbatim.
    raw: z.string().nullable(),
    // The reading of the server, as a plain string (naive ISO, no offset).
    // Null when the server could not read `raw`.
    iso: z.string().nullable(),
    // Why the server could not read `raw`. Null when it could read it.
    // `not_stated_order` = the date contradicts the month-day-year order that a
    // BNY notice always uses. `unrecognised` = no known date format matches the
    // text. `out_of_range` = the parts are outside the range of a real date.
    problem: z.enum(['not_stated_order', 'unrecognised', 'out_of_range']).nullable(),
});
export type CoacNoticeDate = z.infer<typeof noticeDateSchema>;

export const coacActionDraftRowSchema = z.object({
    id: z.string(),
    securityName: z.string(),
    securityId: z.string().nullable(),
    eventType: z.string(),
    sourceReferenceId: z.string(),
    // The dates of the notice. Each one carries the verbatim text, the reading
    // of the server, and the reason for a failed reading.
    responseDeadline: noticeDateSchema,
    exDate: noticeDateSchema,
    recordDate: noticeDateSchema,
    payDate: noticeDateSchema,
    narrative: z.string().nullable(),
    // The options that CEDAR read off the notice. Ops confirms or corrects
    // them beside the thumb, and the approval carries them to the live
    // corporate action. The three states above hold here too.
    electionOptions: coacElectionOptionsSchema.nullable(),
    wealthManager: z.string().nullable(),
    sourceUploadIds: z.array(z.string()),
    fromEmail: z.string().nullable(),
    messageSubject: z.string().nullable(),
    messageReceivedAt: z.string().nullable(),
    correctionNote: z.string().nullable(),
    materialisedCoacActionId: z.string().nullable(),
    createdAt: z.string(),
    reviewStatus: z.enum(['pending', 'approved', 'rejected', 'revision_requested']).nullable(),
    reviewedBy: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    rejectionReason: z.string().nullable(),
});
export type CoacActionDraftRow = z.infer<typeof coacActionDraftRowSchema>;

// ---------------------------------------------------------------------------
// Ops notifications — the in-app inbox surfaced as a bell on the Corporate
// Actions header. First kind: 'coac_votes_complete'.
// ---------------------------------------------------------------------------

export const opsNotificationSchema = z.object({
    id: z.string(),
    kind: z.string(),
    title: z.string(),
    body: z.string().nullable(),
    coacActionId: z.string().nullable(),
    createdAt: z.string(),
    readAt: z.string().nullable(),
});
export type OpsNotification = z.infer<typeof opsNotificationSchema>;

export const opsNotificationsResponseSchema = z.object({
    notifications: z.array(opsNotificationSchema),
    unread: z.number(),
});

export const markNotificationsReadRequestSchema = z.object({
    // Mark a single notification read, or all unread when omitted.
    id: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const coacContract = c.router(
    {
        listActions: {
            method: 'GET',
            path: '/coac/actions',
            responses: {
                200: coacActionListResponseSchema,
            },
            summary:
                'CoacAction rows (presentation projection) the routing list and the Ops/Desk inboxes render',
        },
        exposureSummary: {
            method: 'POST',
            path: '/coac/exposure/summary',
            body: coacExposureSummaryRequestSchema,
            responses: {
                200: z.object({ summaries: z.array(coacExposureSummarySchema) }),
            },
            summary:
                'IM-scoped per-security exposure summary (discretionary / advisory / execution-only units, value, accounts) for a set of securities',
        },
        actionExposure: {
            method: 'GET',
            path: '/coac/actions/:id/exposure',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: coacExposureBreakdownSchema,
                404: errorBody,
            },
            summary:
                "IM-scoped holdings breakdown for one CoacAction's security (per-client/account lines split by service mandate)",
        },
        routeActionToIm: {
            method: 'POST',
            path: '/coac/actions/:id/route-to-im',
            pathParams: z.object({ id: z.string() }),
            body: coacRouteToImRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Ops routes a CoacAction to the responsible IM (Pending -> RoutedToIm)',
        },
        routeActionsBulk: {
            method: 'POST',
            path: '/coac/actions/route-bulk',
            body: coacRouteBulkRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true), routed: z.number() }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Ops routes many selected CoacActions to one chosen IM set (multi-select)',
        },
        routeActionsToHoldersBulk: {
            method: 'POST',
            path: '/coac/actions/route-to-holders-bulk',
            body: coacRouteToHoldersBulkRequestSchema,
            responses: {
                200: z.object({
                    ok: z.literal(true),
                    routed: z.number(),
                    // Selected actions whose security had no desk IM holder — Ops
                    // must route these manually.
                    withoutHolders: z.array(z.string()),
                }),
                400: errorBody,
                403: errorBody,
            },
            summary:
                'Ops fans each selected CoacAction out to all desk IMs holding its company (multi-select)',
        },
        confirmActionAssignment: {
            method: 'POST',
            path: '/coac/actions/:id/confirm-assignment',
            pathParams: z.object({ id: z.string() }),
            body: coacConfirmAssignmentRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Ops confirms a proposed CoacAction IM assignment (optionally correcting it)',
        },
        recallActionFromIms: {
            method: 'POST',
            path: '/coac/actions/:id/recall',
            pathParams: z.object({ id: z.string() }),
            body: coacRecallRequestSchema,
            responses: {
                200: coacRecallResponseSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Ops recalls a routed CoacAction from some or all assigned IMs (a full recall returns it to For Review)',
        },
        deleteActionsBulk: {
            method: 'POST',
            path: '/coac/actions/delete-bulk',
            body: coacDeleteBulkRequestSchema,
            responses: {
                200: z.object({
                    ok: z.literal(true),
                    deleted: z.number(),
                    // Selected ids the server refused (not Pending, or already
                    // deleted). Ops sees the split in the toast.
                    skipped: z.array(z.string()),
                }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Ops soft-deletes selected Pending CoacActions (the For Review bulk bar)',
        },
        recordActionDecision: {
            method: 'POST',
            path: '/coac/actions/:id/record-decision',
            pathParams: z.object({ id: z.string() }),
            body: coacRecordDecisionRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'IM records their decision text on a CoacAction (force=true to overwrite)',
        },
        approveActionElection: {
            method: 'POST',
            path: '/coac/actions/:id/approve-election',
            pathParams: z.object({ id: z.string() }),
            body: coacApproveElectionRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'Finalise the election on a CoacAction (terminal: ElectionApproved)',
        },
        defaultActionElection: {
            method: 'POST',
            path: '/coac/actions/:id/default-election',
            pathParams: z.object({ id: z.string() }),
            body: coacDefaultElectionRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary: 'IM elects the default option on a CoacAction (terminal: ElectionDefaulted)',
        },

        // -----------------------------------------------------------------
        // Per-account elections (WP-E) — the per-account decision model.
        // -----------------------------------------------------------------
        listAccountElections: {
            method: 'GET',
            path: '/coac/actions/:id/account-elections',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: z.object({ elections: z.array(coacAccountElectionViewSchema) }),
                404: errorBody,
            },
            summary:
                'The per-account elections recorded against one CoacAction (with client names)',
        },
        recordAccountElection: {
            method: 'POST',
            path: '/coac/actions/:id/account-elections',
            pathParams: z.object({ id: z.string() }),
            body: coacRecordAccountElectionRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true), id: z.string() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                "Record one account's election on a CoacAction (IM ticks an account, picks a decision)",
        },
        actionBreakdown: {
            method: 'POST',
            path: '/coac/actions/:id/breakdown',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}),
            responses: {
                202: z.object({ jobId: z.string() }),
                404: errorBody,
            },
            summary:
                "Queue a CEDAR coac_breakdown AgentJob for a corporate action (IM's My decisions view); poll the GET endpoint for the persisted result",
        },
        actionBreakdownLatest: {
            method: 'GET',
            path: '/coac/actions/:id/breakdown',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: z.object({
                    breakdown: z
                        .object({
                            id: z.string(),
                            breakdown: z.string(),
                            // SEP-349. Null on a breakdown from before SEP-349.
                            explanation: coacBreakdownExplanationSchema.nullable(),
                            generatedAt: z.string(),
                        })
                        .nullable(),
                }),
                404: errorBody,
            },
            summary:
                'The most recent persisted CEDAR breakdown for a corporate action (null when none has been generated)',
        },
        actionResponses: {
            method: 'GET',
            path: '/coac/actions/:id/responses',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: coacResponsesBreakdownSchema,
                404: errorBody,
            },
            summary:
                'Per-IM response breakdown for one CoacAction (voted/total, responded initials, per-account decisions)',
        },
        actionAffectedPortfolios: {
            method: 'GET',
            path: '/coac/actions/:id/affected-portfolios',
            pathParams: z.object({ id: z.string() }),
            responses: {
                200: coacAffectedPortfoliosSchema,
                404: errorBody,
            },
            summary:
                'Ops-only: every portfolio a corporate action affects, answered or not, grouped by IM, then client, then portfolio',
        },
        // SEP-276. Operations adds portfolios to, and removes portfolios
        // from, the affected set of a corporate action in For Review.
        actionImPortfolios: {
            method: 'GET',
            path: '/coac/actions/:id/im-portfolios',
            pathParams: z.object({ id: z.string() }),
            query: z.object({ imUserId: z.string() }),
            responses: {
                200: z.object({ portfolios: z.array(coacImPortfolioSchema) }),
                400: errorBody,
                404: errorBody,
            },
            summary:
                'Ops-only: every portfolio of one IM, with the quantity of the security of the corporate action that the platform found',
        },
        addActionPortfolios: {
            method: 'POST',
            path: '/coac/actions/:id/portfolios/add',
            pathParams: z.object({ id: z.string() }),
            body: coacAddPortfoliosRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Ops-only: add chosen portfolios of one IM to the affected set of a corporate action in For Review',
        },
        removeActionPortfolio: {
            method: 'POST',
            path: '/coac/actions/:id/portfolios/remove',
            pathParams: z.object({ id: z.string() }),
            body: coacRemovePortfolioRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Ops-only: remove one portfolio from the affected set of a corporate action in For Review',
        },
        setActionPortfolioQuantity: {
            method: 'POST',
            path: '/coac/actions/:id/portfolios/quantity',
            pathParams: z.object({ id: z.string() }),
            body: coacSetPortfolioQuantityRequestSchema,
            responses: {
                200: coacActionOkSchema,
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Ops-only: set the quantity of a portfolio that Operations added to a corporate action in For Review',
        },
        listResponses: {
            method: 'GET',
            path: '/coac/responses',
            responses: {
                200: z.object({ rows: z.array(coacResponsesRowSchema) }),
            },
            summary:
                'Flat per-account election rows across every CA — the Ops Responses tab (filterable)',
        },
        responsesSummary: {
            method: 'GET',
            path: '/coac/responses/summary',
            responses: {
                200: z.object({ rows: z.array(coacResponsesSummaryRowSchema) }),
            },
            summary:
                'Per-CA response roll-up (voted/total accounts + responded IM initials) for the Routing list columns',
        },
        completeAction: {
            method: 'POST',
            path: '/coac/actions/:id/complete',
            pathParams: z.object({ id: z.string() }),
            body: coacCompleteActionRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true), noVoteCount: z.number() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
                // SEP-349. The instruction is not sent, or the reply of the
                // custodian is not attached.
                409: errorBody,
            },
            summary:
                'Ops finalises a CA after the custodian reply is attached (No-Vote sweep on deadline-passed accounts, then opsStatus -> Completed). Desk goes read-only.',
        },

        // -----------------------------------------------------------------
        // CoacActionDraft — AgentArtefact + Reviewable CA drafts emitted by
        // MASON's `coac_pdf_extraction` job (ADR-0022 #271 #272). Approve /
        // reject go through `POST /agent/reviewables/decisions` (kind
        // `coac-action-draft`), not a bespoke route — this is read-only.
        // -----------------------------------------------------------------
        listActionDrafts: {
            method: 'GET',
            path: '/coac/action-drafts',
            responses: {
                200: z.object({
                    drafts: z.array(coacActionDraftRowSchema),
                }),
            },
            summary:
                'List CoacActionDraft rows (MASON Haiku-extracted CA notices, any review status)',
        },

        // -----------------------------------------------------------------
        // Ops notifications inbox (the header bell).
        // -----------------------------------------------------------------
        listNotifications: {
            method: 'GET',
            path: '/coac/notifications',
            responses: {
                200: opsNotificationsResponseSchema,
            },
            summary: 'Ops notifications inbox (most-recent first) plus the unread count',
        },
        markNotificationsRead: {
            method: 'POST',
            path: '/coac/notifications/read',
            body: markNotificationsReadRequestSchema,
            responses: {
                200: z.object({ ok: z.literal(true) }),
            },
            summary: 'Mark one Ops notification (by id) or all unread ones as read',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
